import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function DealRadarCallout() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section
      ref={ref}
      className="px-4"
      style={{ background: '#1A2332', paddingTop: 0, paddingBottom: '2.5rem' }}
      aria-labelledby="deal-radar-heading"
    >
      {/* Visual bridge from white section above */}
      <div
        style={{
          height: '4px',
          background: 'linear-gradient(90deg, transparent 0%, #E8B84B 30%, #F5C842 50%, #E8B84B 70%, transparent 100%)',
          marginBottom: '0',
        }}
        aria-hidden="true"
      />
      <div
        style={{
          height: '32px',
          background: 'linear-gradient(to bottom, rgba(26,35,50,0.0) 0%, #1A2332 100%)',
          marginBottom: '0',
        }}
        aria-hidden="true"
      />

      <div className="max-w-7xl mx-auto pt-5 sm:pt-8">
        {/* Section label */}
        <div className="text-center mb-6 sm:mb-12">
          <span className="inline-flex items-center gap-2 bg-[#E8B84B]/10 border border-[#E8B84B]/30 text-[#E8B84B] text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full mb-3">
            <span aria-hidden="true">⭐</span>
            First Dibs — Email Exclusive
          </span>
          <h2
            id="deal-radar-heading"
            className="text-2xl sm:text-4xl font-bold text-white mb-2 sm:mb-4"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            The Best Cars Are Gone{' '}
            <span className="text-[#E8B84B]">Before You Even See Them.</span>
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">
            Drop your email and get a private heads-up the moment a matching vehicle rolls in — hours before it hits the public listing.
          </p>
        </div>

        <div
          className={`grid lg:grid-cols-2 gap-6 sm:gap-10 items-center transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {/* Left: Benefits + CTA */}
          <div>
            <ul className="space-y-3 sm:space-y-4 mb-5 sm:mb-8">
              {[
                {
                  icon: '🔍',
                  text: "Tell us your budget, make, model & mileage — we'll watch the lot for you",
                },
                {
                  icon: '🔔',
                  text: 'Email alert within minutes of arrival — not days',
                },
                {
                  icon: '⏱️',
                  text: '24–48 hour exclusive window before the car goes live online',
                },
              ].map(item => (
                <li key={item.text} className="flex items-start gap-3 sm:gap-4">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg bg-[#E8B84B]/10 border border-[#E8B84B]/20 flex-shrink-0 mt-0.5 text-base sm:text-lg">
                    <span aria-hidden="true">{item.icon}</span>
                  </div>
                  <span className="text-white/75 text-sm leading-relaxed pt-1">{item.text}</span>
                </li>
              ))}
            </ul>

            <Link
              to="/deal-radar"
              className="inline-flex items-center gap-2 bg-[#E8B84B] hover:bg-[#D4A43A] text-[#1A2332] px-6 py-3 sm:px-8 sm:py-4 rounded-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer text-sm sm:text-base"
              style={{ fontFamily: 'Oswald, sans-serif' }}
            >
              <i className="ri-star-fill text-white text-lg" aria-hidden="true"></i>
              Claim Your First Dibs Spot
              <span aria-hidden="true">→</span>
            </Link>
            <p className="text-white/70 text-xs mt-2 sm:mt-3">Free. No spam. Cancel anytime.</p>
          </div>

          {/* Right: Feature Cards */}
          <div className="space-y-3 sm:space-y-4">
            {[
              {
                icon: '🔔',
                title: "You'll Know Before Anyone Else",
                desc: "We ping you the moment your car lands — sometimes before it's even been washed. That's how early we're talking.",
              },
              {
                icon: '🎛️',
                title: 'Scarily Precise Filters',
                desc: 'Price ceiling, mileage cap, body style, fuel type, color — if you can dream it, you can filter for it.',
              },
              {
                icon: '⏱️',
                title: 'A 24–48 Hour Head Start',
                desc: "While everyone else is browsing listings, you're already scheduling a test drive. That's the First Dibs advantage.",
              },
            ].map((card, i) => (
              <div
                key={card.title}
                className="rounded-xl p-4 sm:p-5 border border-white/10 hover:border-[#E8B84B]/40 transition-all duration-300 hover:shadow-lg cursor-default"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  transitionDelay: isVisible ? `${i * 80}ms` : '0ms',
                }}
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#E8B84B]/10 border border-[#E8B84B]/20 text-lg sm:text-xl">
                    <span aria-hidden="true">{card.icon}</span>
                  </div>
                  <div>
                    <h3
                      className="font-bold text-sm text-white mb-0.5 sm:mb-1"
                      style={{ fontFamily: 'Oswald, sans-serif' }}
                    >
                      {card.title}
                    </h3>
                    <p className="text-white/75 text-sm leading-relaxed">{card.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
